import React, { FC, useState, Dispatch, SetStateAction, useEffect } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import LoadingWrapper from '../../layout/LoadingWrapper';
import PreviousReportInfo from './PreviousReportInfo';
import IssueReport from '../model';
import axios from '../../utils/axios.utils';

interface IssueReportModalProps {
  reportableId: string;
  reportableType: string;
  reviewDialogOpen: boolean;
  setReviewDialogOpen: Dispatch<SetStateAction<boolean>>;
}

const IssueReportModal: FC<IssueReportModalProps> = (props) => {
  let { reportableId, reportableType, reviewDialogOpen, setReviewDialogOpen } = props;

  let [formValues, setFormValues] = useState<any>({});
  let [formErrors, setFormErrors] = useState('');
  let [recentlySubmitted, setRecentlySubmitted] = useState(false);
  let [previousReports, setPreviousReports] = useState<IssueReport[]>([]);
  let [submitting, setSubmitting] = useState<boolean>(false);

  const fetchExistingRequests = async () => {
    const response = await axios.get<string, any>('/issue_reports', {
      params: {
        'ransack[reportable_id_eq]': reportableId,
        'ransack[reportable_type_eq]': reportableType,
      },
    });
    setPreviousReports(response.data.result);
  };

  useEffect(() => {
    if (reportableId) {
      setFormValues({ ...formValues, reportable_id: reportableId, reportable_type: reportableType });
      fetchExistingRequests();
    }
  }, [reportableId]);

  useEffect(() => {
    setRecentlySubmitted(false);
  }, [reviewDialogOpen]);

  const handleSubmitIssueReport = async () => {
    setRecentlySubmitted(true);
    setSubmitting(true);
    try {
      let response: any = await axios.post('/issue_reports', { issue_report: formValues });
      setFormValues({});
      setPreviousReports(response.data.result);
      setSubmitting(false);
    } catch (error: any) {
      setFormErrors(error);
    }
  };

  return (
    <Dialog
      open={reviewDialogOpen}
      onClose={() => setReviewDialogOpen(false)}
      aria-labelledby="review-dialog-title"
      aria-describedby="review-dialog-description"
      fullWidth
    >
      <DialogTitle id="review-dialog-title">Report Issue</DialogTitle>
      <DialogContent>
        <DialogContentText id="review-dialog-description">Enter any additional info</DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="reviewMessage"
          label="Message"
          type="text"
          multiline
          rows={6}
          fullWidth
          variant="outlined"
          value={formValues?.message}
          onChange={(e) => setFormValues({ ...formValues, message: e.target.value })}
        />
        {formErrors && <p style={{ color: 'red' }}>{formErrors}</p>}
        {previousReports?.length > 0 && (
          <Typography style={{ marginTop: 10, marginLeft: 6, fontWeight: 'bold' }}>Previous Reports</Typography>
        )}
        <LoadingWrapper isLoading={submitting} message="Submitting Report">
          {previousReports?.map((report) => (
            <div style={{ marginBottom: 6 }}>
              <PreviousReportInfo issueReport={report} />
            </div>
          ))}
        </LoadingWrapper>
      </DialogContent>
      <DialogActions>
        {recentlySubmitted ? (
          <Button color="success" variant="contained" onClick={() => setReviewDialogOpen(false)}>
            Close
          </Button>
        ) : (
          <Button color="secondary" variant="contained" onClick={() => setReviewDialogOpen(false)}>
            Cancel
          </Button>
        )}
        <Button color="primary" variant="contained" disabled={recentlySubmitted} onClick={handleSubmitIssueReport}>
          Report Issue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueReportModal;
